/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The Ops Console list toolbar controls (the VM list's search, All / On / Off, filter selects and
 * count): 34 px controls, 6 px radius, slate-300 borders. A filter that is not at its default is
 * drawn in the brand tint so a filtered list is obvious at a glance.
 *
 * useListQuery keeps the filters in the URL query (?q=...&status=...), so a filtered list can be
 * linked to and Back returns to it.
 */

import * as React from "react";
import styled, { css } from "styled-components";
import { useSearchParams } from "react-router";
import { Check, ChevronDown, RefreshCw, Search, X } from "lucide-react";

import Checkbox from "@src/components/ui/Checkbox";
import {
  MenuPanel,
  Portal,
  T9,
  usePopover,
} from "@src/components/ui/Lists/ListTable";

// ----------------------------------------------------------------- layout

const ToolbarWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  padding: 12px 16px;
  flex-shrink: 0;
  border-bottom: 1px solid ${T9.slate200};
  background: var(--t9-bg-surface);
`;
const ListToolbar = ({ children }: { children: React.ReactNode }) => (
  <ToolbarWrapper data-test-id="listToolbar">{children}</ToolbarWrapper>
);
export default ListToolbar;

export const ToolbarSpacer = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;
export const ToolbarLabel = styled.span`
  font-size: 13px;
  color: ${T9.slate500};
  white-space: nowrap;
`;
export const ToolbarCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${T9.slate700};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const controlBase = css`
  height: 34px;
  box-sizing: border-box;
  border-radius: 6px;
  border: 1px solid ${T9.slate300};
  background: var(--t9-bg-surface);
  font: inherit;
  font-size: 13px;
  color: ${T9.slate900};
  &:focus-visible {
    outline: none;
    border-color: ${T9.accent500};
    box-shadow: 0 0 0 2px ${T9.accent500};
  }
`;

// ----------------------------------------------------------------- search

const SearchWrapper = styled.div<{ width: number }>`
  position: relative;
  width: ${props => props.width}px;
  max-width: 100%;
  flex-shrink: 0;
  svg.search {
    position: absolute;
    left: 10px;
    top: 10px;
    color: ${T9.slate400};
    pointer-events: none;
  }
`;
const SearchField = styled.input`
  ${controlBase}
  width: 100%;
  padding: 0 30px 0 32px;
  &::placeholder {
    color: ${T9.slate400};
  }
  &:focus {
    outline: none;
    border-color: ${T9.accent500};
    box-shadow: 0 0 0 2px ${T9.accent500};
  }
`;
const ClearSearch = styled.button`
  position: absolute;
  right: 6px;
  top: 7px;
  width: 20px;
  height: 20px;
  border: 0;
  padding: 0;
  border-radius: 4px;
  background: transparent;
  color: ${T9.slate400};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: ${T9.slate100};
    color: ${T9.slate700};
  }
`;
type SearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  width?: number;
};
export const ToolbarSearch = ({ value, onChange, placeholder, width }: SearchProps) => (
  <SearchWrapper width={width || 260}>
    <Search className="search" size={14} />
    <SearchField
      type="search"
      value={value}
      placeholder={placeholder || "Search"}
      aria-label={placeholder || "Search"}
      data-test-id="listToolbar-search"
      onChange={e => onChange(e.target.value)}
    />
    {value ? (
      <ClearSearch type="button" aria-label="Clear search" onClick={() => onChange("")}>
        <X size={14} />
      </ClearSearch>
    ) : null}
  </SearchWrapper>
);

// -------------------------------------------------------------- segmented

const SegmentedWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 34px;
  box-sizing: border-box;
  padding: 3px;
  border-radius: 6px;
  background: ${T9.slate100};
  flex-shrink: 0;
`;
const Segment = styled.button<{ selected?: boolean }>`
  height: 28px;
  padding: 0 10px;
  border: 0;
  border-radius: 4px;
  font: inherit;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: ${props => (props.selected ? 600 : 500)};
  color: ${props => (props.selected ? T9.slate900 : T9.slate500)};
  background: ${props => (props.selected ? "var(--t9-bg-surface)" : "transparent")};
  box-shadow: ${props => (props.selected ? "0 1px 2px rgb(0 0 0 / 0.08)" : "none")};
  &:hover {
    color: ${T9.slate900};
  }
`;
const SegmentCount = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${T9.slate500};
  font-variant-numeric: tabular-nums;
`;
export type ToolbarOption = { value: string; label: string; count?: number };
type SegmentedProps = {
  options: ToolbarOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
};
export const Segmented = ({ options, value, onChange, label }: SegmentedProps) => (
  <SegmentedWrapper role="radiogroup" aria-label={label} data-test-id="listToolbar-segmented">
    {options.map(o => (
      <Segment
        key={o.value}
        type="button"
        role="radio"
        aria-checked={o.value === value}
        selected={o.value === value}
        onClick={() => onChange(o.value)}
      >
        {o.label}
        {o.count !== undefined ? <SegmentCount>{o.count}</SegmentCount> : null}
      </Segment>
    ))}
  </SegmentedWrapper>
);

// ----------------------------------------------------------- filter select

const SelectButton = styled.button<{ active?: boolean; open?: boolean }>`
  ${controlBase}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px 0 10px;
  cursor: pointer;
  white-space: nowrap;
  max-width: 260px;
  flex-shrink: 0;
  ${props =>
    props.active
      ? css`
          border-color: ${T9.brand300};
          background: ${T9.brand50};
        `
      : ""}
  ${props =>
    props.open
      ? css`
          border-color: ${T9.accent500};
          box-shadow: 0 0 0 2px ${T9.accent500};
        `
      : ""}
  &:hover {
    border-color: ${props => (props.open ? T9.accent500 : T9.slate400)};
  }
  .prefix {
    color: ${T9.slate500};
  }
  .value {
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: ${props => (props.active ? 600 : 400)};
    color: ${props => (props.active ? T9.brand700 : T9.slate900)};
  }
  svg {
    flex-shrink: 0;
    color: ${T9.slate500};
  }
`;
const Option = styled.button<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 32px;
  padding: 6px 10px;
  border: 0;
  border-radius: 6px;
  background: ${props => (props.selected ? T9.brand50 : "transparent")};
  font: inherit;
  font-size: 13px;
  text-align: left;
  white-space: nowrap;
  color: ${T9.slate700};
  cursor: pointer;
  &:hover {
    background: ${props => (props.selected ? T9.brand50 : T9.slate100)};
  }
  .label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .count {
    font-size: 12px;
    color: ${T9.slate500};
    font-variant-numeric: tabular-nums;
  }
  .check {
    width: 16px;
    color: ${T9.brand600};
  }
`;
const PanelFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 6px 2px 6px;
  margin-top: 4px;
  border-top: 1px solid ${T9.slate100};
`;
const LinkButton = styled.button`
  border: 0;
  background: none;
  padding: 4px;
  font: inherit;
  font-size: 12px;
  color: ${T9.brand600};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  &:disabled {
    color: ${T9.slate400};
    cursor: default;
    text-decoration: none;
  }
`;

type FilterSelectProps = {
  /** Shown before the value: "Type: Replica". */
  label: string;
  options: ToolbarOption[];
  /** One value (single select) or several (multiple). "" / [] means no filter. */
  value: string | string[];
  onChange: (value: any) => void;
  multiple?: boolean;
  /** The text for "no filter" ("All", "Any time"...). */
  allLabel?: string;
  /** The option value that means "no filter" in a single select. */
  defaultValue?: string;
  testId?: string;
};
export const FilterSelect = (props: FilterSelectProps) => {
  const { open, setOpen, pos, anchorRef, panelRef } = usePopover("left");
  const allLabel = props.allLabel || "All";
  const defaultValue = props.defaultValue ?? "";
  const values = Array.isArray(props.value) ? props.value : [props.value];
  const selected = props.multiple ? values.filter(Boolean) : values;
  const active = props.multiple ? selected.length > 0 : props.value !== defaultValue;

  let valueLabel = allLabel;
  if (props.multiple) {
    if (selected.length === 1) {
      valueLabel = props.options.find(o => o.value === selected[0])?.label || selected[0];
    } else if (selected.length > 1) {
      valueLabel = `${selected.length} selected`;
    }
  } else {
    valueLabel = props.options.find(o => o.value === props.value)?.label || allLabel;
  }

  const toggleValue = (value: string) => {
    if (!props.multiple) {
      props.onChange(value);
      setOpen(false);
      return;
    }
    const next = selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value];
    props.onChange(next);
  };

  return (
    <>
      <SelectButton
        ref={anchorRef}
        type="button"
        active={active}
        open={open}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-test-id={props.testId || `listToolbar-filter-${props.label}`}
        onClick={() => setOpen(!open)}
      >
        <span className="prefix">{props.label}</span>
        <span className="value">{valueLabel}</span>
        <ChevronDown size={14} />
      </SelectButton>
      {open ? (
        <Portal>
          <MenuPanel
            ref={panelRef}
            role="listbox"
            aria-multiselectable={props.multiple}
            style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
            data-test-id="listToolbar-filterMenu"
          >
            {!props.multiple && !props.options.find(o => o.value === defaultValue) ? (
              <Option type="button" selected={props.value === defaultValue} onClick={() => toggleValue(defaultValue)}>
                <span className="check">{props.value === defaultValue ? <Check size={14} /> : null}</span>
                <span className="label">{allLabel}</span>
              </Option>
            ) : null}
            {props.options.map(o => {
              const isSelected = props.multiple ? selected.includes(o.value) : props.value === o.value;
              return (
                <Option
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  selected={!props.multiple && isSelected}
                  onClick={() => toggleValue(o.value)}
                >
                  {props.multiple ? (
                    <Checkbox checked={isSelected} />
                  ) : (
                    <span className="check">{isSelected ? <Check size={14} /> : null}</span>
                  )}
                  <span className="label">{o.label}</span>
                  {o.count !== undefined ? <span className="count">{o.count}</span> : null}
                </Option>
              );
            })}
            {props.multiple ? (
              <PanelFooter>
                <LinkButton type="button" disabled={!selected.length} onClick={() => props.onChange([])}>
                  Clear
                </LinkButton>
                <LinkButton type="button" onClick={() => setOpen(false)}>
                  Done
                </LinkButton>
              </PanelFooter>
            ) : null}
          </MenuPanel>
        </Portal>
      ) : null}
    </>
  );
};

// ------------------------------------------------------------ date range

const DateGroup = styled.div<{ active?: boolean }>`
  ${controlBase}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 4px 0 10px;
  flex-shrink: 0;
  ${props =>
    props.active
      ? css`
          border-color: ${T9.brand300};
          background: ${T9.brand50};
        `
      : ""}
  .prefix {
    color: ${T9.slate500};
    margin-right: 2px;
  }
  .dash {
    color: ${T9.slate400};
  }
  input {
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 13px;
    color: ${T9.slate900};
    height: 30px;
    padding: 0 2px;
    width: 118px;
    &:focus {
      outline: none;
    }
    &::-webkit-calendar-picker-indicator {
      opacity: 0.5;
      cursor: pointer;
    }
  }
`;
type DateRangeProps = {
  label?: string;
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
};
/** Two native date inputs ("YYYY-MM-DD"), as one control. */
export const ToolbarDateRange = ({ label, from, to, onChange }: DateRangeProps) => (
  <DateGroup active={Boolean(from || to)} data-test-id="listToolbar-dates">
    <span className="prefix">{label || "Date"}</span>
    <input
      type="date"
      aria-label="From"
      value={from}
      max={to || undefined}
      onChange={e => onChange({ from: e.target.value, to })}
    />
    <span className="dash">–</span>
    <input
      type="date"
      aria-label="To"
      value={to}
      min={from || undefined}
      onChange={e => onChange({ from, to: e.target.value })}
    />
  </DateGroup>
);

/** True when `date` falls between the two "YYYY-MM-DD" days (both inclusive, local time). */
export const withinDays = (date: Date | null, from: string, to: string) => {
  if (!from && !to) return true;
  if (!date) return false;
  if (from && date.getTime() < new Date(`${from}T00:00:00`).getTime()) return false;
  if (to && date.getTime() > new Date(`${to}T23:59:59.999`).getTime()) return false;
  return true;
};

// ---------------------------------------------------------------- buttons

const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 34px;
  padding: 0 8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font: inherit;
  font-size: 13px;
  color: ${T9.slate600};
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${T9.slate100};
    color: ${T9.slate900};
  }
`;
export const ClearFiltersButton = ({ onClick }: { onClick: () => void }) => (
  <GhostButton type="button" onClick={onClick} data-test-id="listToolbar-clear">
    <X size={14} />
    Clear filters
  </GhostButton>
);

export const ToolbarIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  box-sizing: border-box;
  border: 1px solid ${T9.slate300};
  border-radius: 6px;
  background: var(--t9-bg-surface);
  color: ${T9.slate600};
  cursor: pointer;
  flex-shrink: 0;
  &:hover {
    background: ${T9.slate50};
    color: ${T9.slate900};
  }
`;
export const RefreshButton = ({ onClick }: { onClick: () => void }) => (
  <ToolbarIconButton type="button" title="Refresh" aria-label="Refresh" onClick={onClick} data-test-id="listToolbar-refresh">
    <RefreshCw size={14} />
  </ToolbarIconButton>
);

// ------------------------------------------------------------- URL query

export type ListQuery = {
  /** One value ("" when absent). */
  get: (key: string) => string;
  /** A comma separated list. */
  getList: (key: string) => string[];
  /** Sets (or, with "" / [] / null, removes) keys; the page and the history entry are replaced. */
  set: (changes: { [key: string]: string | string[] | null | undefined }) => void;
  /** The query string, for the effect dependencies. */
  key: string;
};

export const useListQuery = (): ListQuery => {
  const [params, setParams] = useSearchParams();
  return {
    get: k => params.get(k) || "",
    getList: k => (params.get(k) || "").split(",").filter(Boolean),
    set: changes => {
      setParams(
        prev => {
          const next = new URLSearchParams(prev);
          Object.keys(changes).forEach(k => {
            const v = changes[k];
            const str = Array.isArray(v) ? v.join(",") : v;
            if (str) next.set(k, str);
            else next.delete(k);
          });
          return next;
        },
        { replace: true },
      );
    },
    key: params.toString(),
  };
};

/** A ListQuery over a plain object, for tests and pages rendered outside a router. */
export const staticListQuery = (initial: { [key: string]: string } = {}): ListQuery => {
  const values: { [key: string]: string } = { ...initial };
  return {
    get: k => values[k] || "",
    getList: k => (values[k] || "").split(",").filter(Boolean),
    set: changes => {
      Object.keys(changes).forEach(k => {
        const v = changes[k];
        const str = Array.isArray(v) ? v.join(",") : v;
        if (str) values[k] = str;
        else delete values[k];
      });
    },
    key: JSON.stringify(values),
  };
};

// ---------------------------------------------------------- time windows

export const ACTIVITY_OPTIONS: ToolbarOption[] = [
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];
const WINDOW_MS: { [k: string]: number } = {
  "24h": 24 * 3600 * 1000,
  "7d": 7 * 24 * 3600 * 1000,
  "30d": 30 * 24 * 3600 * 1000,
};
/** True when `date` is within the window ("24h" / "7d" / "30d"); any date passes without a window. */
export const withinWindow = (date: Date | null, window: string, now = Date.now()) => {
  if (!window || !WINDOW_MS[window]) return true;
  if (!date) return false;
  return now - date.getTime() <= WINDOW_MS[window];
};

/** Case-insensitive "every word appears somewhere in these fields". */
export const matchesSearch = (text: string, fields: (string | null | undefined)[]) => {
  const words = text.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = fields.filter(Boolean).join(" \u0001 ").toLowerCase();
  return words.every(w => hay.includes(w));
};
